'use client';

import Container from '@/components/layout/container/container';
import { Button } from '@/components/ui/button/button';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import styles from './listing.module.scss';
import { ListingFilters, type FilterConfig } from './listingFilters';
import { ListingItemCard } from './listingItem';

const ITEMS_PER_PAGE = 4;

export type ListingItemsFeaturedImage = {
  url?: string | null;
  title?: string | null;
};

export type ListingItem = {
  title: string;
  slug: string;
  url: string;
  itemType?: string | null;
  filterValues: Record<string, string[]>;
  venue?: string | null;
  address?: string | null;
  eventsDate?: string | null;
  eventsEndDate?: string | null;
  dateDisplayOverride?: string | null;
  featuredImage?: ListingItemsFeaturedImage;
};

type ListingProps = {
  items?: ListingItem[];
  filters?: FilterConfig[];
  showPastDate?: boolean;
  banner?: ReactNode;
  className?: string;
};

export const Listing = ({
  items = [],
  filters = [],
  showPastDate = false,
  banner,
  className,
}: ListingProps) => {
  const baseItems = useMemo(() => {
    if (showPastDate) return items;

    const now = new Date();

    return items.filter(({ eventsDate, eventsEndDate, itemType }) => {
      // Only filter past dates for events and webinars, not resources
      if (itemType !== 'Event' && itemType !== 'Webinar') return true;

      const dateStr = eventsEndDate ?? eventsDate;
      if (!dateStr) return true;

      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return true;

      return date >= now;
    });
  }, [items, showPastDate]);

  const [filteredItems, setFilteredItems] = useState(baseItems);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  // const filteredItems = items;

  const hasAnyFilterOptions = useMemo(() => {
    if (!filters.length) return false;

    return filters.some(({ key }) =>
      baseItems.some((item) => {
        const values = item.filterValues[key];
        return Array.isArray(values) && values.length > 0;
      }),
    );
  }, [filters, baseItems]);

  useEffect(() => {
    setFilteredItems(baseItems);
    setVisibleCount(ITEMS_PER_PAGE);
  }, [baseItems]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
        }
      },
      { threshold: 1 },
    );

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, []);

  const visibleItems = filteredItems.slice(0, visibleCount);

  const clearFilters = () => {
    setFilteredItems(baseItems);
    setVisibleCount(ITEMS_PER_PAGE);
    setFilterResetKey((prev) => prev + 1);
  };

  return (
    <section className={cn(styles.section, className)}>
      <Container className={styles.filterMobile}>
        <ListingFilters
          items={baseItems}
          mobile={true}
          filters={filters}
          resetKey={filterResetKey}
          onFilterChange={(newItems) => {
            setFilteredItems(newItems);
            setVisibleCount(ITEMS_PER_PAGE);
          }}
          onClearFilters={clearFilters}
        />
      </Container>
      <Container className={cn(styles.container, !hasAnyFilterOptions && styles.noFilters)}>
        <div className={cn(styles.listing, banner && styles.hasBanner)}>
          {visibleItems.length === 0 ? (
            <div className={styles.noResults}>
              <Image
                src="/images/noResults.svg"
                alt="No results found"
                width={128}
                height={123}
                priority
              />
              {filteredItems.length < baseItems.length ? (
                <>
                  <h4>Nothing found for your criteria</h4>
                  <p>Let&apos;s clear your filters and start over</p>
                  <Button withArrow onClick={clearFilters}>
                    Clear all filters
                  </Button>
                </>
              ) : (
                <h4>No results available</h4>
              )}
            </div>
          ) : (
            visibleItems.map((item, i) => <ListingItemCard key={item.slug + i} item={item} />)
          )}

          {/* Infinite scroll trigger */}
          <div ref={loaderRef} />
        </div>

        <div className={styles.filters}>
          <ListingFilters
            items={baseItems}
            mobile={false}
            filters={filters}
            resetKey={filterResetKey}
            onFilterChange={(newItems) => {
              setFilteredItems(newItems);
              setVisibleCount(ITEMS_PER_PAGE);
            }}
            onClearFilters={clearFilters}
          />
        </div>
      </Container>
      {banner && <Container className={styles.banner}>{banner}</Container>}
    </section>
  );
};
